import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, MoreVertical, Send, Sparkles, User, 
  Phone, Clock, Tag, ChevronDown, CheckCheck, RefreshCw, MessageSquare,
  Archive, AlertTriangle, Trash2, Info, UserPlus, Check, X, Edit2, AlertCircle,
  CalendarCheck, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';
import ConversationStatePanel from '../../components/ConversationStatePanel';
import BookingStatusBlock from '../../components/BookingStatusBlock';
// IMPROVEMENT 5: Timezone utilities for local time display
import { formatInboxTimestamp, formatMessageTimestamp, formatToLocalDateTime } from '../../lib/timezone';

const ConversationsPage = () => {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [stateFilter, setStateFilter] = useState(searchParams.get('filter') || '');
  const messagesEndRef = useRef(null);
  
  // AI Settings state
  const [aiSettings, setAiSettings] = useState({ enabled: true, human_in_loop: true });
  
  // Conversation State Engine state
  const [conversationState, setConversationState] = useState(null);
  const [loadingState, setLoadingState] = useState(false);
  
  // Dialog states
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  
  // Draft message editing state
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingDraftContent, setEditingDraftContent] = useState('');
  const [processingDraft, setProcessingDraft] = useState(null);
  
  // IMPROVEMENT 5: Organization timezone for local time display
  // Default to Asia/Kuala_Lumpur if not set
  const [orgTimezone, setOrgTimezone] = useState('Asia/Kuala_Lumpur');
  
  // Booking confirmation state
  const [processingBooking, setProcessingBooking] = useState(false);
  
  // Role checks
  const isOperator = user?.role === 'operator' && !user?.is_super_admin;
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin;

  // Handle URL filter parameter
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setStateFilter(filterParam);
    }
    const selectedParam = searchParams.get('selected');
    if (selectedParam && conversations.length > 0) {
      const conv = conversations.find(c => c.id === selectedParam);
      if (conv) setSelectedConversation(conv);
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    fetchConversations();
    fetchAISettings();
    fetchOrgTimezone();  // IMPROVEMENT 5: Fetch org timezone
  }, [statusFilter, stateFilter]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      fetchConversationState(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // IMPROVEMENT 5: Fetch organization timezone for local time display
  const fetchOrgTimezone = async () => {
    try {
      const response = await axios.get(`${API}/settings/ai-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Use org timezone or fallback to Asia/Kuala_Lumpur
      const timezone = response.data?.default_timezone || 'Asia/Kuala_Lumpur';
      setOrgTimezone(timezone);
    } catch (error) {
      console.error('Failed to fetch org timezone:', error);
      // Fallback to Asia/Kuala_Lumpur as per requirement
      setOrgTimezone('Asia/Kuala_Lumpur');
    }
  };

  const fetchAISettings = async () => {
    try {
      const response = await axios.get(`${API}/ai/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both ai_enabled and enabled for backward compatibility
      const data = response.data;
      setAiSettings({
        enabled: data.ai_enabled ?? data.enabled ?? true,
        human_in_loop: data.human_in_loop ?? true,
        model: data.model ?? 'gpt-5.2'
      });
    } catch (error) {
      console.error('Failed to fetch AI settings:', error);
      // Fail-safe: disable AI if settings fetch fails
      setAiSettings({ enabled: false, human_in_loop: true, model: 'gpt-5.2' });
    }
  };
  
  const fetchConversationState = async (conversationId) => {
    setLoadingState(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationState(response.data);
    } catch (error) {
      console.error('Failed to fetch conversation state:', error);
      setConversationState(null);
    } finally {
      setLoadingState(false);
    }
  };
  
  const handleRefreshState = () => {
    if (selectedConversation) {
      fetchConversationState(selectedConversation.id);
    }
  };
  
  const clearFilter = () => {
    setStateFilter('');
    setSearchParams({});
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      let url = `${API}/conversations`;
      const params = {};
      
      // Use state filter from URL if present
      if (stateFilter) {
        url = `${API}/dashboard/conversations-by-status`;
        params.status_filter = stateFilter;
      } else if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setConversations(response.data);
      if (response.data.length > 0 && !selectedConversation) {
        setSelectedConversation(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendMessage = async (content = newMessage) => {
    if (!content.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        conversation_id: selectedConversation.id,
        content: content,
        is_from_customer: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage('');
      setAiSuggestion(null);
      fetchMessages(selectedConversation.id);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const generateAISuggestion = async () => {
    if (!selectedConversation) return;
    
    // Check if AI is enabled
    if (!aiSettings.enabled) {
      toast.error('AI is disabled in settings');
      return;
    }
    
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/ai/generate-reply`, 
        { conversation_id: selectedConversation.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.ai_enabled === false) {
        toast.error('AI is disabled in organization settings');
      } else if (response.data.suggested_reply) {
        setAiSuggestion(response.data.suggested_reply);
        toast.success('AI suggestion generated');
      } else if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.error('Could not generate suggestion');
      }
    } catch (error) {
      toast.error('Failed to generate AI suggestion');
    } finally {
      setGeneratingAI(false);
    }
  };

  const updateConversationStatus = async (status) => {
    if (!selectedConversation) return;
    
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/status`, 
        JSON.stringify(status),
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      fetchConversations();
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Conversation action handlers
  const handleViewInfo = async () => {
    if (!selectedConversation) return;
    try {
      const response = await axios.get(`${API}/conversations/${selectedConversation.id}/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationInfo(response.data);
      setShowInfoDialog(true);
    } catch (error) {
      toast.error('Failed to fetch conversation info');
    }
  };

  const handleOpenAssignDialog = async () => {
    if (!selectedConversation) return;
    try {
      const response = await axios.get(`${API}/conversations/${selectedConversation.id}/operators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data);
      setSelectedOperator(selectedConversation.assigned_to || '');
      setShowAssignDialog(true);
    } catch (error) {
      toast.error('Failed to fetch operators');
    }
  };

  const handleAssignConversation = async () => {
    if (!selectedConversation || !selectedOperator) return;
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/assign`, 
        JSON.stringify(selectedOperator),
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      fetchConversations();
      setShowAssignDialog(false);
      toast.success('Conversation assigned');
    } catch (error) {
      toast.error('Failed to assign conversation');
    }
  };

  const handleArchiveConversation = async () => {
    if (!selectedConversation) return;
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchConversations();
      setSelectedConversation(null);
      toast.success('Conversation archived');
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  const handleMarkSpam = async () => {
    if (!selectedConversation) return;
    try {
      await axios.put(`${API}/conversations/${selectedConversation.id}/spam`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchConversations();
      setSelectedConversation(null);
      toast.success('Marked as spam');
    } catch (error) {
      toast.error('Failed to mark as spam');
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    try {
      await axios.delete(`${API}/conversations/${selectedConversation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchConversations();
      setSelectedConversation(null);
      setShowDeleteDialog(false);
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const canDelete = user?.role === 'owner' || user?.role === 'admin' || user?.is_super_admin;

  // Draft message handlers
  const handleApproveDraft = async (messageId) => {
    setProcessingDraft(messageId);
    try {
      await axios.put(`${API}/messages/${messageId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message approved and sent');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      toast.error('Failed to approve message');
    } finally {
      setProcessingDraft(null);
    }
  };

  const handleRejectDraft = async (messageId) => {
    setProcessingDraft(messageId);
    try {
      await axios.put(`${API}/messages/${messageId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draft message rejected');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      toast.error('Failed to reject message');
    } finally {
      setProcessingDraft(null);
    }
  };

  const handleStartEditDraft = (message) => {
    setEditingDraftId(message.id);
    setEditingDraftContent(message.content);
  };

  const handleSaveEditDraft = async (messageId) => {
    setProcessingDraft(messageId);
    try {
      await axios.put(`${API}/messages/${messageId}/edit-draft`, 
        { content: editingDraftContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Draft updated');
      setEditingDraftId(null);
      setEditingDraftContent('');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      toast.error('Failed to update draft');
    } finally {
      setProcessingDraft(null);
    }
  };

  const handleCancelEditDraft = () => {
    setEditingDraftId(null);
    setEditingDraftContent('');
  };

  // Booking confirmation handlers
  const handleConfirmBooking = async () => {
    if (!selectedConversation) return;
    
    setProcessingBooking(true);
    try {
      const resp = await axios.post(
        `${API}/conversations/${selectedConversation.id}/mark-booked`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = resp.data || {};
      if (data.calendar_event_id) {
        toast.success(`Booked on calendar · event ${String(data.calendar_event_id).slice(0, 12)}…`);
      } else {
        toast.success('Booking confirmed. Analytics updated.');
      }
      fetchConversations();
      if (selectedConversation.id) {
        const response = await axios.get(`${API}/conversations/${selectedConversation.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedConversation(response.data);
        fetchMessages(selectedConversation.id);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || {};
      const code = error.response?.status;
      // Phase 2.3.5 Req #4: Show conflict + smart_suggestion clearly in UI + insert chat note.
      if (code === 409 && detail?.error === 'slot_conflict') {
        const sug = detail.smart_suggestion;
        const sugTxt = sug ? ` Suggested: ${sug.day || ''} ${sug.date || ''} at ${sug.time || ''}.` : '';
        const firstConflict = (detail.conflicts && detail.conflicts[0]) || {};
        toast.error(
          `Slot unavailable — already booked.${sugTxt}`,
          { duration: 10000 }
        );
        // Write a staff-only note into the conversation so the conflict is visible in chat history too.
        try {
          await axios.post(
            `${API}/conversations/${selectedConversation.id}/staff-note`,
            {
              note: `❌ Booking rejected — slot is already taken${firstConflict.summary ? ` by "${firstConflict.summary}"` : ''}.${sugTxt}`,
              category: 'calendar_conflict',
              metadata: { conflicts: detail.conflicts || [], smart_suggestion: sug || null },
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          fetchMessages(selectedConversation.id);
        } catch (noteErr) { /* non-fatal */ }
      } else if (code === 422 && detail?.error === 'outside_operating_hours') {
        const sug = detail.smart_suggestion;
        const sugTxt = sug ? ` Try: ${sug.day || ''} ${sug.date || ''} at ${sug.time || ''}.` : '';
        toast.error(`Slot outside operating hours — ${detail.reason || ''}.${sugTxt}`, { duration: 10000 });
      } else if (code === 409 && detail?.error === 'state_conflict') {
        toast.error('Booking already being processed by another operator.');
      } else {
        console.error('Failed to confirm booking:', error);
        toast.error(typeof detail === 'string' ? detail : (detail?.reason || 'Failed to confirm booking'));
      }
      // Refresh to reflect state changes (e.g., calendar_sync_failed)
      try {
        const response = await axios.get(`${API}/conversations/${selectedConversation.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedConversation(response.data);
      } catch {}
    } finally {
      setProcessingBooking(false);
    }
  };

  const handleDismissBooking = async () => {
    if (!selectedConversation) return;
    
    setProcessingBooking(true);
    try {
      await axios.post(
        `${API}/conversations/${selectedConversation.id}/dismiss-booking`,
        { event_id: selectedConversation.booking_detection_event_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Booking detection dismissed');
      // Refresh conversation to clear the banner
      fetchConversations();
      if (selectedConversation.id) {
        const response = await axios.get(`${API}/conversations/${selectedConversation.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedConversation(response.data);
      }
    } catch (error) {
      console.error('Failed to dismiss booking:', error);
      toast.error('Failed to dismiss booking detection');
    } finally {
      setProcessingBooking(false);
    }
  };

  const filteredConversations = conversations.filter(conv => 
    conv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.customer_phone?.includes(searchQuery)
  );

  // IMPROVEMENT 5: Updated formatTime to use organization timezone
  // PART 2 FIX: Accept optional timezone override from conversation
  const formatTime = (isoString, convTimezone = null) => {
    if (!isoString) return '';
    // Use conversation's timezone if provided, otherwise use org default
    const tz = convTimezone || orgTimezone || 'Asia/Kuala_Lumpur';
    return formatInboxTimestamp(isoString, tz);
  };
  
  // IMPROVEMENT 5: Format message time using organization timezone
  // PART 2 FIX: Accept optional timezone override from conversation
  const formatMsgTime = (isoString, convTimezone = null) => {
    if (!isoString) return '';
    const tz = convTimezone || orgTimezone || 'Asia/Kuala_Lumpur';
    return formatMessageTimestamp(isoString, tz);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div data-testid="conversations-page">
      {/* Active Filter Banner */}
      {stateFilter && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              Showing conversations filtered by: <strong className="capitalize">{stateFilter.replace('_', ' ')}</strong>
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilter} className="text-amber-700 hover:text-amber-900">
            Clear Filter
          </Button>
        </div>
      )}
      
      <div className="h-[calc(100vh-8rem)] flex">
      {/* Conversation List */}
      <div className="w-full md:w-96 border-r flex flex-col bg-card">
        {/* Search & Filter */}
        <div className="p-4 border-b space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="conversation-search"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'open', 'pending', 'closed'].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'bg-emerald-700' : ''}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No conversations yet</p>
              <p className="text-sm mt-2">Conversations from WhatsApp will appear here</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                role="button"
                tabIndex={0}
                className={`p-4 border-b cursor-pointer transition-colors select-none ${
                  selectedConversation?.id === conv.id 
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-l-emerald-500' 
                    : 'hover:bg-muted'
                }`}
                data-testid={`conversation-item-${conv.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium">{conv.customer_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{conv.customer_phone}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{formatTime(conv.last_message_at, conv.org_timezone)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant={conv.status === 'open' ? 'default' : 'secondary'} className={conv.status === 'open' ? 'bg-emerald-600' : ''}>
                    {conv.status}
                  </Badge>
                  {conv.unread_count > 0 && (
                    <span className="w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Chat View */}
      <div className="hidden md:flex flex-1 flex-col bg-background">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="h-16 border-b px-6 flex items-center justify-between bg-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                  <User className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="font-medium">{selectedConversation.customer_name || 'Unknown Customer'}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Phone className="w-3 h-3" />
                    {selectedConversation.customer_phone}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2" data-testid="status-dropdown">
                      <Badge className={selectedConversation.status === 'open' ? 'bg-emerald-600' : ''}>
                        {selectedConversation.status}
                      </Badge>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => updateConversationStatus('open')}>Open</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => updateConversationStatus('pending')}>Pending</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => updateConversationStatus('closed')}>Closed</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* 3-dot Menu with Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="conversation-actions-menu">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleOpenAssignDialog}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Assign / Reassign
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleArchiveConversation}>
                      <Archive className="w-4 h-4 mr-2" />
                      Archive Conversation
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleMarkSpam}>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Mark as Spam
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleViewInfo}>
                      <Info className="w-4 h-4 mr-2" />
                      View Info
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {canDelete ? (
                      <DropdownMenuItem 
                        onClick={() => setShowDeleteDialog(true)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Conversation
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled className="text-muted-foreground">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete (Admin only)
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Booking Detection Banner - Shows only when booking detected but NOT yet confirmed */}
            {(selectedConversation?.booking_detected || selectedConversation?.booking_candidate) &&
             !selectedConversation?.booking_confirmed &&
             selectedConversation?.booking_status !== 'booked' && (
              <div 
                className="px-6 py-4 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800 animate-slide-down"
                data-testid="booking-detection-banner"
              >
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-800/50 rounded-full">
                      <CalendarCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                          Booking Request Detected
                        </span>
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-800/50 dark:text-emerald-300 dark:border-emerald-700">
                          Pending
                        </Badge>
                      </div>
                      {selectedConversation?.booking_info && (
                        <div className="text-sm text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-2 flex-wrap">
                          {selectedConversation.booking_info.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {selectedConversation.booking_info.date}
                            </span>
                          )}
                          {selectedConversation.booking_info.time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {selectedConversation.booking_info.time}
                            </span>
                          )}
                          {selectedConversation.booking_info.doctor && (
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              {selectedConversation.booking_info.doctor}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDismissBooking}
                      disabled={processingBooking}
                      className="h-9 text-slate-600 hover:text-slate-800 dark:text-slate-300"
                      data-testid="dismiss-booking-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleConfirmBooking}
                      disabled={processingBooking}
                      className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="confirm-booking-btn"
                    >
                      {processingBooking ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-1" />
                      )}
                      Mark as Booked
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Booking Confirmed Status */}
            {selectedConversation?.booking_status === 'booked' && (
              <div 
                className="px-6 py-3 bg-emerald-100 dark:bg-emerald-900/30 border-b border-emerald-200 dark:border-emerald-800"
                data-testid="booking-confirmed-status"
              >
                <div className="max-w-3xl mx-auto flex items-center gap-3">
                  <CalendarCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    Booking Confirmed
                  </span>
                  <Badge className="bg-emerald-600 text-white">
                    Booked
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Messages */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.is_staff_note ? 'justify-center' : msg.is_from_customer ? 'justify-start' : 'justify-end'}`}
                  >
                    {/* Staff note — Phase 2.3.5 Req #4 — visible only to operators */}
                    {msg.is_staff_note ? (
                      <div className="max-w-[85%] my-1" data-testid={`staff-note-${msg.id}`}>
                        <div className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
                          msg.staff_note_category === 'calendar_conflict'
                            ? 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/30 dark:text-red-100'
                            : 'bg-slate-50 border-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                        }`}>
                          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
                              Staff note {msg.staff_note_category ? `· ${msg.staff_note_category.replace('_',' ')}` : ''}
                            </div>
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                            {msg.staff_note_metadata?.smart_suggestion && (
                              <div className="mt-1 text-[11px] font-medium">
                                💡 Alternative: {msg.staff_note_metadata.smart_suggestion.day} {msg.staff_note_metadata.smart_suggestion.date} at {msg.staff_note_metadata.smart_suggestion.time}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : msg.is_draft ? (
                      <div className="max-w-[70%] relative">
                        {/* Draft indicator banner */}
                        <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 mb-2 bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 rounded-t-lg border border-amber-200 dark:border-amber-800">
                          <AlertCircle className="w-3.5 h-3.5" />
                          <span className="font-medium">DRAFT - Pending Approval</span>
                          {msg.ai_generated && (
                            <>
                              <span className="text-amber-400">•</span>
                              <Sparkles className="w-3 h-3" />
                              <span>AI Generated</span>
                            </>
                          )}
                        </div>
                        
                        {/* Draft message content */}
                        <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 border-t-0 rounded-b-lg px-4 py-3">
                          {editingDraftId === msg.id ? (
                            <div className="space-y-3">
                              <textarea
                                value={editingDraftContent}
                                onChange={(e) => setEditingDraftContent(e.target.value)}
                                className="w-full min-h-[80px] p-2 text-sm border rounded-md bg-white dark:bg-slate-800 resize-none"
                                data-testid={`edit-draft-textarea-${msg.id}`}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveEditDraft(msg.id)}
                                  disabled={processingDraft === msg.id}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`save-draft-btn-${msg.id}`}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEditDraft}
                                  data-testid={`cancel-edit-btn-${msg.id}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700 dark:text-slate-300">{msg.content}</p>
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                                <span className="text-xs text-muted-foreground">{formatMsgTime(msg.created_at, selectedConversation?.org_timezone)}</span>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleStartEditDraft(msg)}
                                    disabled={processingDraft === msg.id}
                                    className="h-8 px-3 gap-1 text-slate-600 hover:text-slate-800"
                                    data-testid={`edit-draft-btn-${msg.id}`}
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectDraft(msg.id)}
                                    disabled={processingDraft === msg.id}
                                    className="h-8 px-3 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                                    data-testid={`reject-draft-btn-${msg.id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    Reject
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveDraft(msg.id)}
                                    disabled={processingDraft === msg.id}
                                    className="h-8 px-3 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid={`approve-draft-btn-${msg.id}`}
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    Approve & Send
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Regular message display */
                      <div className={`max-w-[70%] ${
                        msg.is_from_customer 
                          ? 'message-customer' 
                          : msg.ai_generated 
                            ? 'message-ai' 
                            : 'message-business'
                      }`}>
                        {/* PART 5: Message failure indicator */}
                        {!msg.is_from_customer && msg.delivery_status === 'failed' && (
                          <div className="flex items-center gap-1 text-xs text-red-500 dark:text-red-400 mb-1 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded" data-testid={`message-failed-${msg.id}`}>
                            <AlertTriangle className="w-3 h-3" />
                            <span>Message failed to send</span>
                            {msg.whatsapp_delivery?.error && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="w-3 h-3 ml-1 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">{msg.whatsapp_delivery.error}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        )}
                        {msg.ai_generated && (
                          <div className="flex items-center gap-1 text-xs opacity-80 mb-1">
                            <Sparkles className="w-3 h-3" />
                            AI Generated
                          </div>
                        )}
                        <p className="text-sm">{msg.content}</p>
                        <div className="flex items-center justify-end gap-1 mt-1 opacity-70">
                          <span className="text-xs">{formatMsgTime(msg.created_at, selectedConversation?.org_timezone)}</span>
                          {!msg.is_from_customer && (
                            msg.delivery_status === 'failed' ? (
                              <AlertTriangle className="w-3 h-3 text-red-500" />
                            ) : msg.delivery_status === 'sent' ? (
                              <CheckCheck className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <CheckCheck className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            {/* AI Suggestion */}
            {aiSuggestion && (
              <div className="px-6 py-3 border-t bg-lime-50 dark:bg-lime-900/20 animate-slide-up">
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center gap-2 text-sm text-lime-700 dark:text-lime-400 mb-2">
                    <Sparkles className="w-4 h-4" />
                    AI Suggestion
                  </div>
                  <p className="text-sm mb-2 text-slate-700 dark:text-slate-300">{aiSuggestion}</p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-emerald-700 hover:bg-emerald-800"
                      onClick={() => handleSendMessage(aiSuggestion)}
                      data-testid="use-ai-suggestion"
                    >
                      Use This Reply
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setNewMessage(aiSuggestion)}
                    >
                      Edit First
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setAiSuggestion(null)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Message Input */}
            <div className="p-4 border-t bg-card">
              <div className="max-w-3xl mx-auto flex gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={generateAISuggestion}
                          disabled={generatingAI || !aiSettings.enabled}
                          className={`flex-shrink-0 ${!aiSettings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid="generate-ai-btn"
                        >
                          <Sparkles className={`w-5 h-5 ${generatingAI ? 'animate-spin' : ''} ${aiSettings.enabled ? 'text-lime-600' : 'text-muted-foreground'}`} />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {aiSettings.enabled ? 'Generate AI suggestion' : 'AI is disabled in settings'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button 
                  onClick={() => handleSendMessage()}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Conversation State Panel - Right Side */}
      {selectedConversation && (
        <div className="hidden lg:block w-80 border-l bg-card overflow-y-auto">
          <div className="p-4 space-y-3">
            <BookingStatusBlock conv={selectedConversation} />
            <ConversationStatePanel 
              state={conversationState} 
              onRefresh={handleRefreshState}
            />
          </div>
        </div>
      )}
    </div>

      {/* Mobile notice for actions */}
      <div className="md:hidden fixed bottom-20 left-4 right-4 bg-amber-100 dark:bg-amber-900/30 p-3 rounded-lg text-center text-sm text-amber-800 dark:text-amber-200">
        Conversation actions are available on desktop
      </div>

      {/* Conversation Info Dialog */}
      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conversation Info</DialogTitle>
            <DialogDescription>Details about this conversation</DialogDescription>
          </DialogHeader>
          {conversationInfo && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Conversation ID:</span>
                <span className="font-mono text-xs break-all">{conversationInfo.conversation_id}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Organization ID:</span>
                <span className="font-mono text-xs break-all">{conversationInfo.organization_id}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Customer:</span>
                <span>{conversationInfo.customer_name || 'Unknown'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Phone:</span>
                <span>{conversationInfo.customer_phone}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant="outline">{conversationInfo.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Messages:</span>
                <span>{conversationInfo.message_count}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Created:</span>
                <span>{formatToLocalDateTime(conversationInfo.created_at, orgTimezone)}</span>
              </div>
              {conversationInfo.assigned_to && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Assigned To:</span>
                  <span>{conversationInfo.assigned_to.name}</span>
                </div>
              )}
              {conversationInfo.intent && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Intent:</span>
                  <Badge variant="secondary">{conversationInfo.intent}</Badge>
                </div>
              )}
              {conversationInfo.sentiment && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Sentiment:</span>
                  <Badge variant="secondary">{conversationInfo.sentiment}</Badge>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Conversation</DialogTitle>
            <DialogDescription>Select a team member to handle this conversation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedOperator} onValueChange={setSelectedOperator}>
              <SelectTrigger>
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent>
                {operators.map(op => (
                  <SelectItem key={op.id} value={op.id}>
                    {op.name} ({op.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignConversation} disabled={!selectedOperator} className="bg-emerald-700">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Conversation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this conversation? This action cannot be undone and will delete all messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConversation}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConversationsPage;
